"use client";

import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Group,
    NumberInput,
    Radio,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Textarea,
    ThemeIcon,
    Title,
} from "@mantine/core";
import {
    IconBriefcase,
    IconCalendarEvent,
    IconCheck,
    IconClock,
    IconMapPin,
    IconMoodSmile,
    IconSchool,
    IconSparkles,
    IconUsers,
} from "@tabler/icons-react";
import type { DevisFormPayload, PublicActivityOption } from "../types";
import { EVENT_TYPE_OPTIONS } from "../constants";
import styles from "./QuoteRequestForm.module.scss";

type Props = {
    initialActivities: PublicActivityOption[];
};

const initialForm: DevisFormPayload = {
    contact: {
        fullName: "",
        jobTitle: "",
        companyName: "",
        billingAddress: "",
        vatNumber: "",
        email: "",
        phone: "",
    },
    event: {
        type: "sortie_scolaire",
        preferredDate: "",
        preferredTime: "",
        participantsCount: 10,
        averageAge: "",
    },
    location: {
        locationType: "client_establishment",
        eventAddress: "",
    },
    activities: {
        wantsAdvice: false,
        selectedIds: [],
    },
    freeMessage: "",
};

export default function QuoteRequestForm({ initialActivities }: Props) {
    const [form, setForm] = useState<DevisFormPayload>(initialForm);
    const [loading, setLoading] = useState(false);
    const [successRef, setSuccessRef] = useState<string | null>(null);
    const [error, setError] = useState("");

    const groupedActivities = useMemo(() => {
        const booking = initialActivities.filter(
            (item) => item.source === "booking_product"
        );
        const rdv = initialActivities.filter((item) => item.source === "rdv_activity");
        return { booking, rdv };
    }, [initialActivities]);

    useEffect(() => {
        if (form.activities.wantsAdvice) {
            setForm((prev) => ({
                ...prev,
                activities: {
                    ...prev.activities,
                    selectedIds: [],
                },
            }));
        }
    }, [form.activities.wantsAdvice]);

    function updateField(path: string, value: any) {
        setForm((prev) => {
            const clone = structuredClone(prev);
            const keys = path.split(".");
            let current: any = clone;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return clone;
        });
    }

    function toggleActivity(id: string) {
        setForm((prev) => {
            const exists = prev.activities.selectedIds.includes(id);

            return {
                ...prev,
                activities: {
                    ...prev.activities,
                    selectedIds: exists
                        ? prev.activities.selectedIds.filter((item) => item !== id)
                        : [...prev.activities.selectedIds, id],
                },
            };
        });
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccessRef(null);

        try {
            const res = await fetch("/api/admin/crud/quote_requests", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(form),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data?.message || "Une erreur est survenue.");
            }

            setSuccessRef(data?.requestNumber || "Demande envoyée");
            setForm(initialForm);
        } catch (err: any) {
            setError(err?.message || "Impossible d’envoyer la demande.");
        } finally {
            setLoading(false);
        }
    }

    const selectedCount = form.activities.selectedIds.length;

    return (
        <div className={styles.page}>
            <div className={styles.shell}>
                <div className={styles.hero}>
                    <div className={styles.heroBadge}>
                        <ThemeIcon radius="xl" variant="light" size={34}>
                            <IconSparkles size={18} />
                        </ThemeIcon>
                        <Text fw={700}>Ateliers Mini Monkeys</Text>
                    </div>

                    <Title order={1} className={styles.title}>
                        Demande de devis école / entreprise
                    </Title>

                    <Text className={styles.subtitle}>
                        Décris ton événement en quelques minutes. Notre équipe te prépare une
                        proposition adaptée, claire et rapide.
                    </Text>

                    <div className={styles.heroStats}>
                        <div className={styles.heroStat}>
                            <IconSchool size={18} />
                            <span>Écoles</span>
                        </div>
                        <div className={styles.heroStat}>
                            <IconBriefcase size={18} />
                            <span>Entreprises</span>
                        </div>
                        <div className={styles.heroStat}>
                            <IconMoodSmile size={18} />
                            <span>Ateliers créatifs</span>
                        </div>
                    </div>
                </div>

                {successRef && (
                    <Alert
                        color="green"
                        radius="lg"
                        variant="light"
                        icon={<IconCheck size={18} />}
                        className={styles.alert}
                    >
                        Merci, votre demande a bien été envoyée.
                        <strong> {successRef}</strong>
                    </Alert>
                )}

                {error && (
                    <Alert color="red" radius="lg" variant="light" className={styles.alert}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit} className={styles.form}>
                    <Card className={styles.section} radius="xl" shadow="sm" padding="xl">
                        <Group className={styles.sectionHeader} wrap="nowrap">
                            <ThemeIcon size={42} radius="xl" className={styles.iconWrap}>
                                <IconBriefcase size={20} />
                            </ThemeIcon>
                            <div>
                                <Text fw={800} className={styles.sectionTitle}>
                                    1. Informations de contact
                                </Text>
                                <Text className={styles.sectionText}>
                                    Qui organise l’événement et où devons-nous envoyer le devis.
                                </Text>
                            </div>
                        </Group>

                        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                            <TextInput
                                label="Nom et prénom"
                                placeholder="Ex. Sophie Martin"
                                value={form.contact.fullName}
                                onChange={(e) => updateField("contact.fullName", e.currentTarget.value)}
                                required
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Fonction / poste"
                                placeholder="Ex. Responsable pédagogique"
                                value={form.contact.jobTitle}
                                onChange={(e) => updateField("contact.jobTitle", e.currentTarget.value)}
                                required
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Entreprise / École / Organisation"
                                placeholder="Ex. École Saint Pierre"
                                value={form.contact.companyName}
                                onChange={(e) =>
                                    updateField("contact.companyName", e.currentTarget.value)
                                }
                                required
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Numéro de TVA"
                                placeholder="Optionnel"
                                value={form.contact.vatNumber || ""}
                                onChange={(e) => updateField("contact.vatNumber", e.currentTarget.value)}
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Email"
                                placeholder="contact@exemple.com"
                                type="email"
                                value={form.contact.email}
                                onChange={(e) => updateField("contact.email", e.currentTarget.value)}
                                required
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Téléphone"
                                placeholder="06 00 00 00 00"
                                value={form.contact.phone}
                                onChange={(e) => updateField("contact.phone", e.currentTarget.value)}
                                required
                                radius="lg"
                                size="md"
                            />
                        </SimpleGrid>

                        <Textarea
                            label="Adresse de facturation"
                            placeholder="Adresse complète"
                            value={form.contact.billingAddress}
                            onChange={(e) =>
                                updateField("contact.billingAddress", e.currentTarget.value)
                            }
                            required
                            radius="lg"
                            size="md"
                            minRows={4}
                            mt="md"
                        />
                    </Card>

                    <Card className={styles.section} radius="xl" shadow="sm" padding="xl">
                        <Group className={styles.sectionHeader} wrap="nowrap">
                            <ThemeIcon size={42} radius="xl" className={styles.iconWrap}>
                                <IconCalendarEvent size={20} />
                            </ThemeIcon>
                            <div>
                                <Text fw={800} className={styles.sectionTitle}>
                                    2. Informations sur l’événement
                                </Text>
                                <Text className={styles.sectionText}>
                                    Donne-nous le contexte pour qu’on te propose le bon format.
                                </Text>
                            </div>
                        </Group>

                        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                            <Select
                                label="Type d’événement / occasion"
                                placeholder="Choisir"
                                data={EVENT_TYPE_OPTIONS}
                                value={form.event.type}
                                onChange={(value) => updateField("event.type", value || "")}
                                required
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Date souhaitée"
                                type="date"
                                value={form.event.preferredDate}
                                onChange={(e) =>
                                    updateField("event.preferredDate", e.currentTarget.value)
                                }
                                required
                                radius="lg"
                                size="md"
                            />

                            <TextInput
                                label="Horaires souhaités"
                                placeholder="Ex. 14h00 - 16h00"
                                value={form.event.preferredTime}
                                onChange={(e) =>
                                    updateField("event.preferredTime", e.currentTarget.value)
                                }
                                required
                                radius="lg"
                                size="md"
                            />

                            <NumberInput
                                label="Nombre de participants"
                                min={1}
                                value={form.event.participantsCount}
                                onChange={(value) =>
                                    updateField("event.participantsCount", Number(value || 1))
                                }
                                required
                                radius="lg"
                                size="md"
                            />
                        </SimpleGrid>

                        <TextInput
                            mt="md"
                            label="Âge moyen des participants"
                            placeholder="Ex. 7 ans / adultes / 10-12 ans"
                            value={form.event.averageAge}
                            onChange={(e) => updateField("event.averageAge", e.currentTarget.value)}
                            required
                            radius="lg"
                            size="md"
                        />
                    </Card>

                    <Card className={styles.section} radius="xl" shadow="sm" padding="xl">
                        <Group className={styles.sectionHeader} wrap="nowrap">
                            <ThemeIcon size={42} radius="xl" className={styles.iconWrap}>
                                <IconMapPin size={20} />
                            </ThemeIcon>
                            <div>
                                <Text fw={800} className={styles.sectionTitle}>
                                    3. Lieu de l’événement
                                </Text>
                                <Text className={styles.sectionText}>
                                    Où l’activité doit-elle avoir lieu ?
                                </Text>
                            </div>
                        </Group>

                        <Radio.Group
                            value={form.location.locationType}
                            onChange={(value) => updateField("location.locationType", value)}
                        >
                            <Stack gap="sm">
                                <Radio
                                    value="client_establishment"
                                    label="Dans notre établissement (école / entreprise)"
                                    size="md"
                                />
                                <Radio value="other_place" label="Autre lieu" size="md" />
                            </Stack>
                        </Radio.Group>

                        {form.location.locationType === "other_place" && (
                            <Textarea
                                mt="md"
                                label="Adresse de l’événement"
                                placeholder="Adresse complète du lieu"
                                value={form.location.eventAddress || ""}
                                onChange={(e) =>
                                    updateField("location.eventAddress", e.currentTarget.value)
                                }
                                minRows={4}
                                required
                                radius="lg"
                                size="md"
                            />
                        )}
                    </Card>

                    <Card className={styles.section} radius="xl" shadow="sm" padding="xl">
                        <Group className={styles.sectionHeader} wrap="nowrap">
                            <ThemeIcon size={42} radius="xl" className={styles.iconWrap}>
                                <IconSparkles size={20} />
                            </ThemeIcon>
                            <div>
                                <Text fw={800} className={styles.sectionTitle}>
                                    4. Choix de l’activité
                                </Text>
                                <Text className={styles.sectionText}>
                                    Sélectionne une ou plusieurs activités, ou demande simplement un
                                    conseil.
                                </Text>
                            </div>
                        </Group>

                        <div className={styles.adviceBox}>
                            <Checkbox
                                checked={form.activities.wantsAdvice}
                                onChange={(e) =>
                                    updateField("activities.wantsAdvice", e.currentTarget.checked)
                                }
                                label="Je ne sais pas encore — j’aimerais être conseillé.e"
                                size="md"
                            />
                        </div>

                        {!form.activities.wantsAdvice && (
                            <>
                                <Group justify="space-between" className={styles.selectionBar}>
                                    <Text fw={700}>Activités disponibles</Text>
                                    <Text size="sm" c="dimmed">
                                        {selectedCount} sélection{selectedCount > 1 ? "s" : ""}
                                    </Text>
                                </Group>

                                {!!groupedActivities.booking.length && (
                                    <div className={styles.groupBlock}>
                                        <Text className={styles.groupTitle}>Ateliers</Text>
                                        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                                            {groupedActivities.booking.map((activity) => {
                                                const checked = form.activities.selectedIds.includes(activity.id);

                                                return (
                                                    <button
                                                        type="button"
                                                        key={activity.id}
                                                        onClick={() => toggleActivity(activity.id)}
                                                        className={`${styles.activityCard} ${checked ? styles.activityCardActive : ""
                                                            }`}
                                                    >
                                                        <div className={styles.activityImageWrap}>
                                                            {activity.imageUrl ? (
                                                                <img
                                                                    src={activity.imageUrl}
                                                                    alt={activity.label}
                                                                    className={styles.activityImage}
                                                                />
                                                            ) : (
                                                                <div className={styles.activityPlaceholder}>
                                                                    <IconSparkles size={22} />
                                                                </div>
                                                            )}

                                                            {checked && (
                                                                <div className={styles.activityCheck}>
                                                                    <IconCheck size={16} />
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className={styles.activityContent}>
                                                            <Text fw={800} className={styles.activityTitle}>
                                                                {activity.label}
                                                            </Text>

                                                            {!!activity.description && (
                                                                <Text className={styles.activityDescription} lineClamp={4}>
                                                                    {activity.description}
                                                                </Text>
                                                            )}

                                                            <Group gap="xs" className={styles.activityMeta}>
                                                                {activity.durationMin ? (
                                                                    <span className={styles.metaChip}>
                                                                        <IconClock size={14} />
                                                                        {activity.durationMin} min
                                                                    </span>
                                                                ) : null}

                                                                {activity.ageMin ? (
                                                                    <span className={styles.metaChip}>
                                                                        <IconUsers size={14} />
                                                                        Dès {activity.ageMin} ans
                                                                    </span>
                                                                ) : null}
                                                            </Group>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </SimpleGrid>
                                    </div>
                                )}

                                {!!groupedActivities.rdv.length && (
                                    <div className={styles.groupBlock}>
                                        <Text className={styles.groupTitle}>Autres activités</Text>
                                        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                                            {groupedActivities.rdv.map((activity) => {
                                                const checked = form.activities.selectedIds.includes(activity.id);

                                                return (
                                                    <button
                                                        type="button"
                                                        key={activity.id}
                                                        onClick={() => toggleActivity(activity.id)}
                                                        className={`${styles.activityCard} ${checked ? styles.activityCardActive : ""
                                                            }`}
                                                    >
                                                        <div className={styles.activityImageWrap}>
                                                            {activity.imageUrl ? (
                                                                <img
                                                                    src={activity.imageUrl}
                                                                    alt={activity.label}
                                                                    className={styles.activityImage}
                                                                />
                                                            ) : (
                                                                <div className={styles.activityPlaceholder}>
                                                                    <IconSparkles size={22} />
                                                                </div>
                                                            )}

                                                            {checked && (
                                                                <div className={styles.activityCheck}>
                                                                    <IconCheck size={16} />
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className={styles.activityContent}>
                                                            <Text fw={800} className={styles.activityTitle}>
                                                                {activity.label}
                                                            </Text>

                                                            {!!activity.description && (
                                                                <Text className={styles.activityDescription} lineClamp={4}>
                                                                    {activity.description}
                                                                </Text>
                                                            )}

                                                            <Group gap="xs" className={styles.activityMeta}>
                                                                {activity.durationMin ? (
                                                                    <span className={styles.metaChip}>
                                                                        <IconClock size={14} />
                                                                        {activity.durationMin} min
                                                                    </span>
                                                                ) : null}
                                                            </Group>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </SimpleGrid>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>

                    <Card className={styles.section} radius="xl" shadow="sm" padding="xl">
                        <Group className={styles.sectionHeader} wrap="nowrap">
                            <ThemeIcon size={42} radius="xl" className={styles.iconWrap}>
                                <IconUsers size={20} />
                            </ThemeIcon>
                            <div>
                                <Text fw={800} className={styles.sectionTitle}>
                                    5. Message libre
                                </Text>
                                <Text className={styles.sectionText}>
                                    Ajoute tout ce qui peut nous aider à préparer une meilleure
                                    proposition.
                                </Text>
                            </div>
                        </Group>

                        <Textarea
                            label="Décrivez votre projet ou vos besoins"
                            placeholder="Ex. Nous cherchons un atelier créatif de 1h30 pour 24 enfants de CE2, idéalement sur site..."
                            value={form.freeMessage}
                            onChange={(e) => updateField("freeMessage", e.currentTarget.value)}
                            minRows={6}
                            radius="lg"
                            size="md"
                        />
                    </Card>

                    <div className={styles.submitWrap}>
                        <div className={styles.submitNote}>
                            <Text fw={700}>Envoi rapide</Text>
                            <Text size="sm" c="dimmed">
                                Nous vous recontactons avec une proposition adaptée.
                            </Text>
                        </div>

                        <Button
                            type="submit"
                            size="lg"
                            radius="xl"
                            loading={loading}
                            className={styles.submitButton}
                        >
                            Envoyer ma demande
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}